import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { ExternalLink, XCircle, RefreshCw } from "lucide-react";

const CANCELLABLE = ["PENDING_2FA", "PAYMENT_PENDING", "RECEIVED"];

function OrdersTable({ items, empty, testid, loading, onCancel, onRefresh }) {
  return (
    <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
      {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
        items.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid={testid}>{empty}</div> :
        <div className="divide-y divide-[var(--glass-border)]">
          {items.map((o) => (
            <div key={o.order_number || o.order_id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`orders-list-row-${o.order_number || o.order_id}`}>
              <div>
                <div className="font-medium text-[var(--ink)]">{o.scheme_name || o.from_scheme_name}{o.to_scheme_name ? ` → ${o.to_scheme_name}` : ""}</div>
                <div className="text-xs text-[var(--ink-muted)]">
                  Order #{o.order_number || o.order_id} &middot; {formatDateTime(o.created_at)}
                  {o.order_type === "SPREAD" && o.redeem_date ? ` · Auto-redeem on ${o.redeem_date}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
                <div className="font-medium">{o.amount ? formatCurrency(o.amount) : o.qty ? `${o.qty} units` : (o.switch_amount ? formatCurrency(o.switch_amount) : "-")}</div>
                <StatusBadge status={o.status} />
                <Bse2Badge bse2={o.bse2} />
                {o.bse2?.auth_link && <Button asChild size="sm" className="rounded-full bg-[var(--cta)]"><a href={o.bse2.auth_link} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> BSE auth</a></Button>}
                {onRefresh && o.bse2?.bse_order_id && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onRefresh(o.order_number)}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Status</Button>}
                {onCancel && o.order_number && CANCELLABLE.includes(o.status) && (
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => onCancel(o.order_number)} data-testid={`order-cancel-button-${o.order_number}`}>
                    <XCircle className="mr-1 h-3.5 w-3.5" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>}
    </CardContent></Card>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [switches, setSwitches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => clientApi.get("/orders").then((r) => { setOrders(r.data.orders); setSwitches(r.data.switches); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleCancel = async (orderNumber) => {
    try {
      const { data } = await clientApi.post(`/orders/${orderNumber}/cancel`);
      if (data.status === "CANCEL_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE cancellation outcome is pending; do not repeat it.");
      else toast.success(data.bse2?.message || data.bse_remarks || "Order cancellation submitted to BSE");
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const refreshOrder = async (orderNumber) => {
    try {
      const { data } = await clientApi.post(`/orders/${orderNumber}/bse-refresh`);
      toast.success(data.message || `BSE order status: ${data.live_status || "refreshed"}`);
      load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  const [syncing, setSyncing] = useState(false);
  const handleBseSync = async () => {
    setSyncing(true);
    try {
      const { data } = await clientApi.post("/portfolio/bse-sync");
      if (data.status === "SYNCED") {
        toast.success(`BSE sync complete: ${data.matched_ucc} order(s) for your UCC, ${data.linked_local} linked, ${data.allotted_updated} allotments updated`);
      } else if (["FAILED", "NOT_CONFIGURED", "INCOMPLETE"].includes(data.status)) {
        toast.error(data.message || `BSE sync failed with status ${data.status}`);
      } else {
        toast(`BSE sync: ${data.message}`);
      }
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const purchases = orders.filter((o) => o.order_type === "PURCHASE");
  const redemptions = orders.filter((o) => o.order_type === "REDEMPTION");
  const spreads = orders.filter((o) => o.order_type === "SPREAD");

  return (
    <ClientLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Orders</h1>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto"><Button asChild variant="outline" size="sm" className="rounded-full"><Link to="/payments">Payments</Link></Button><Button variant="outline" size="sm" className="rounded-full" onClick={handleBseSync} disabled={syncing} data-testid="orders-bse-sync-button">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync from BSE"}
        </Button></div>
      </div>
      <Tabs defaultValue="purchase">
        <TabsList>
          <TabsTrigger value="purchase" data-testid="orders-tab-purchase">Purchases ({purchases.length})</TabsTrigger>
          <TabsTrigger value="redemption" data-testid="orders-tab-redemption">Redemptions ({redemptions.length})</TabsTrigger>
          <TabsTrigger value="spread" data-testid="orders-tab-spread">Spread ({spreads.length})</TabsTrigger>
          <TabsTrigger value="switch" data-testid="orders-tab-switch">Switches ({switches.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="purchase"><OrdersTable items={purchases} empty="No purchase orders yet." testid="no-purchase-orders" loading={loading} onCancel={handleCancel} onRefresh={refreshOrder} /></TabsContent>
        <TabsContent value="redemption"><OrdersTable items={redemptions} empty="No redemption orders yet." testid="no-redemption-orders" loading={loading} onCancel={handleCancel} onRefresh={refreshOrder} /></TabsContent>
        <TabsContent value="spread"><OrdersTable items={spreads} empty="No historical spread orders." testid="no-spread-orders" loading={loading} onCancel={handleCancel} onRefresh={refreshOrder} /></TabsContent>
        <TabsContent value="switch"><OrdersTable items={switches} empty="No switch orders yet." testid="no-switch-orders" loading={loading} /></TabsContent>
      </Tabs>
    </ClientLayout>
  );
}
